import { useRef, useState, type ReactNode } from "react";

/** Número do dente sob o mouse (ou o foco do teclado). */
function denteDoAlvo(alvo: EventTarget | null): Element | null {
  if (!(alvo instanceof Element)) return null;
  return alvo.closest("[data-dente]") ?? alvo.closest("g[class*='teeth-']");
}
function numeroDoDente(el: Element): string | null {
  return el.getAttribute("data-dente") ?? el.getAttribute("class")?.match(/teeth-(\d{2})/)?.[1] ?? null;
}

/**
 * Tooltip com o número do dente, igual nas duas arcadas. Medido na tela a partir do dente de verdade
 * — o da biblioteca do odontograma usava as coordenadas internas do desenho e aparecia longe do
 * dente, por isso fica desligado lá e este o substitui.
 */
export function TooltipDoDente({ children }: { children: ReactNode }) {
  const caixa = useRef<HTMLDivElement>(null);
  const [dica, setDica] = useState<{ numero: string; x: number; y: number } | null>(null);
  const mostrar = (alvo: EventTarget | null) => {
    const el = denteDoAlvo(alvo);
    const numero = el && numeroDoDente(el);
    if (!el || !numero || !caixa.current) return setDica(null);
    const r = el.getBoundingClientRect();
    const c = caixa.current.getBoundingClientRect();
    setDica({ numero, x: r.left + r.width / 2 - c.left, y: r.top - c.top });
  };
  return (
    <div
      ref={caixa}
      className="relative"
      onMouseOver={(e) => mostrar(e.target)}
      onMouseLeave={() => setDica(null)}
      onFocus={(e) => mostrar(e.target)}
      onBlur={() => setDica(null)}
    >
      {children}
      {dica && (
        <span
          role="tooltip"
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-zinc-200 bg-white px-2 py-0.5 text-[11px] font-semibold tabular-nums text-zinc-800 shadow-lg dark:border-border dark:bg-card dark:text-foreground"
          style={{ left: dica.x, top: dica.y - 4 }}
        >
          {dica.numero}
        </span>
      )}
    </div>
  );
}
